import React from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faPen, faPlus, faPowerOff, faRefresh } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Table from '../../Components/Table/Table';
import { useSecurity } from '../../Contexts/SecurityContext';
import { RolesDataService } from '../../Services/RolesDataService';
import { formatDateTime } from '../../Utils/formaters';
import RolePermissionsModal from './RolePermissionsModal';
import './RolesAdminPage.scss';
import { Permissions } from '../../Security/permissions';

const apiErrorMessage = (error, fallback = 'The request could not be completed.') => {
    const problem = error?.response?.data;
    if (typeof problem === 'string') return problem;
    const validationMessages = problem?.errors
        ? Object.values(problem.errors).flat().filter(Boolean)
        : [];
    return validationMessages[0] || problem?.detail || problem?.title || problem?.message || error?.message || fallback;
};

const reservedRoleNames = new Set(['admin', 'sysadmin']);

const RoleStatusBadge = ({ active }) => (
    <Badge bg={active ? 'success' : 'secondary'}>{active ? 'Active' : 'Inactive'}</Badge>
);

export const RoleFormModal = ({ role, onHide, onSave }) => {
    const [roleName, setRoleName] = React.useState(role?.roleName || '');
    const [saving, setSaving] = React.useState(false);
    const [validated, setValidated] = React.useState(false);
    const [error, setError] = React.useState('');

    const submit = async event => {
        event.preventDefault();
        setValidated(true);
        if (!event.currentTarget.checkValidity()) return;

        setSaving(true);
        setError('');
        try {
            await onSave(roleName.trim());
        } catch (saveError) {
            setError(apiErrorMessage(saveError, 'The role could not be saved.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={saving ? undefined : onHide} backdrop={saving ? 'static' : true} centered>
            <Form noValidate validated={validated} onSubmit={submit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{role ? 'Rename role' : 'Create role'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group controlId="application-role-name">
                        <Form.Label>Role name</Form.Label>
                        <Form.Control
                            required
                            autoFocus
                            minLength={1}
                            maxLength={50}
                            value={roleName}
                            onChange={event => setRoleName(event.target.value)}
                            placeholder="Role name"
                        />
                        <Form.Control.Feedback type="invalid">Enter a role name of up to 50 characters.</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving}>
                        {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save role'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export const RolesAdminContent = () => {
    const { appUser, acquireAccessToken, refreshSecurity, canExecute } = useSecurity();
    const readOnly = !canExecute(Permissions.Security.Roles);
    const [roles, setRoles] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [includeInactive, setIncludeInactive] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');
    const [editing, setEditing] = React.useState(undefined);
    const [permissionsRole, setPermissionsRole] = React.useState(null);
    const [statusChange, setStatusChange] = React.useState(null);
    const [statusSaving, setStatusSaving] = React.useState(false);

    const loadRoles = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await acquireAccessToken();
            setRoles(await RolesDataService.getRoles(token, includeInactive));
        } catch (loadError) {
            setError(apiErrorMessage(loadError, 'Roles could not be loaded.'));
        } finally {
            setLoading(false);
        }
    }, [acquireAccessToken, includeInactive]);

    React.useEffect(() => { loadRoles(); }, [loadRoles]);

    const saveRole = async roleName => {
        if (readOnly) return;
        const token = await acquireAccessToken();
        if (editing?.roleId) {
            await RolesDataService.updateRole(editing.roleId, roleName, token);
            setSuccess('Role renamed successfully.');
            if (editing.roleId === appUser?.roleId) await refreshSecurity();
        } else {
            await RolesDataService.createRole(roleName, token);
            setSuccess('Role created successfully.');
        }
        setEditing(undefined);
        if (editing?.roleId !== appUser?.roleId) await loadRoles();
    };

    const applyStatusChange = async () => {
        if (readOnly) return;
        if (!statusChange) return;
        setStatusSaving(true);
        setError('');
        try {
            const nextStatus = !statusChange.isActive;
            await RolesDataService.setRoleStatus(statusChange.roleId, nextStatus, await acquireAccessToken());
            setSuccess(`Role ${nextStatus ? 'activated' : 'deactivated'} successfully.`);
            setStatusChange(null);
            if (statusChange.roleId === appUser?.roleId) refreshSecurity();
            else await loadRoles();
        } catch (statusError) {
            setError(apiErrorMessage(statusError, 'The role status could not be changed.'));
            setStatusChange(null);
        } finally {
            setStatusSaving(false);
        }
    };

    const visibleRoles = React.useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) return roles;
        return roles.filter(role => String(role.roleName || '').toLowerCase().includes(value));
    }, [roles, search]);

    const columns = React.useMemo(() => [
        {
            header: 'Role',
            accessorKey: 'roleName',
            cell: ({ getValue }) => getValue() || <span className="text-muted">Unnamed legacy role</span>
        },
        { header: 'Status', accessorKey: 'isActive', cell: ({ getValue }) => <RoleStatusBadge active={getValue()} /> },
        {
            header: 'Last updated',
            accessorKey: 'updatedAtUtc',
            cell: ({ row }) => formatDateTime(row.original.updatedAtUtc || row.original.createdAtUtc) || 'Not available'
        },
        {
            header: 'Actions',
            id: 'actions',
            enableSorting: false,
            cell: ({ row }) => {
                const role = row.original;
                const reserved = reservedRoleNames.has(String(role.roleName || '').trim().toLowerCase());
                return (
                    <div className="roles-admin-actions">
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            title={reserved ? 'This reserved role cannot be renamed' : 'Rename role'}
                            aria-label={`Rename ${role.roleName || 'unnamed role'}`}
                            disabled={readOnly || reserved}
                            onClick={() => setEditing(role)}
                        >
                            <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-primary"
                            title="Manage role permissions"
                            aria-label={`Manage permissions for ${role.roleName || 'unnamed role'}`}
                            onClick={() => setPermissionsRole(role)}
                        >
                            <FontAwesomeIcon icon={faKey} />
                        </Button>
                        <Button
                            size="sm"
                            variant={role.isActive ? 'outline-danger' : 'outline-success'}
                            title={role.isActive ? 'Deactivate role' : 'Activate role'}
                            aria-label={`${role.isActive ? 'Deactivate' : 'Activate'} ${role.roleName || 'unnamed role'}`}
                            onClick={() => setStatusChange(role)}
                            disabled={readOnly}
                        >
                            <FontAwesomeIcon icon={faPowerOff} />
                        </Button>
                    </div>
                );
            }
        }
    ], [readOnly]);

    return (
        <div className="roles-admin-page">
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="roles-admin-toolbar">
                <Form.Control
                    type="search"
                    aria-label="Search roles"
                    placeholder="Search roles..."
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                />
                <Form.Check
                    type="switch"
                    id="include-inactive-roles"
                    label="Include inactive"
                    checked={includeInactive}
                    onChange={event => setIncludeInactive(event.target.checked)}
                />
                <Button variant="outline-primary" title="Refresh" aria-label="Refresh roles" onClick={loadRoles} disabled={loading}>
                    <FontAwesomeIcon icon={faRefresh} />
                </Button>
                <Button disabled={readOnly} onClick={() => setEditing(null)}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" />Create role
                </Button>
            </div>

            {loading ? (
                <div className="roles-admin-state"><Spinner animation="border" /><span>Loading roles...</span></div>
            ) : visibleRoles.length ? (
                <Table
                    columns={columns}
                    data={visibleRoles}
                    tableClass="table-hover"
                    headerClass="table-light"
                    containerClass="table-responsive"
                />
            ) : (
                <Alert variant="light" className="roles-admin-state">No roles found.</Alert>
            )}

            {editing !== undefined && (
                <RoleFormModal role={editing} onHide={() => setEditing(undefined)} onSave={saveRole} />
            )}
            {permissionsRole && (
                <RolePermissionsModal
                    role={permissionsRole}
                    onHide={() => setPermissionsRole(null)}
                    onSaved={() => {
                        setPermissionsRole(null);
                        setSuccess('Role permissions updated successfully.');
                    }}
                />
            )}
            {statusChange && (
                <ConfirmationModal
                    show
                    title={`${statusChange.isActive ? 'Deactivate' : 'Activate'} role`}
                    content={`Are you sure you want to ${statusChange.isActive ? 'deactivate' : 'activate'} ${statusChange.roleName || 'this unnamed role'}?`}
                    acceptText={statusSaving ? 'Saving...' : (statusChange.isActive ? 'Deactivate' : 'Activate')}
                    acceptVariant={statusChange.isActive ? 'danger' : 'success'}
                    acceptDisabled={statusSaving}
                    cancelDisabled={statusSaving}
                    closeDisabled={statusSaving}
                    onHide={() => setStatusChange(null)}
                    onCancelClick={() => setStatusChange(null)}
                    onAcceptClick={applyStatusChange}
                />
            )}
        </div>
    );
};

const RolesAdminPage = () => (
    <PageLayout2 title="Role Administration">
        <RolesAdminContent />
    </PageLayout2>
);

export default RolesAdminPage;
